import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

/* Shared "back" control.
   Every back control in the app used to be a hardcoded <Link> to one fixed
   destination, so arriving at a screen from anywhere other than that fixed
   parent sent you somewhere you'd never been (e.g. opening a surah from the
   home-page bookmark, then "All surahs" dropping you on the hifz plan).

   This goes back to wherever the user actually came from, and only falls
   back to `to` when there's no in-app history to return to — a deep link,
   a fresh tab, or a page opened as the session's first route.

   React Router tracks its own position in the history stack as `idx` on
   window.history.state; idx > 0 means there's an earlier in-app entry, so
   navigate(-1) stays inside the app rather than leaving the site.

   `useGoBack` is the same logic as a hook, for flows that need to go back
   programmatically (e.g. finishing the last ayah of a surah). */
export function useGoBack(to) {
  const navigate = useNavigate();
  return () => {
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else navigate(to);
  };
}

export default function BackLink({ to, label, className = "", testid = "back-link" }) {
  const goBack = useGoBack(to);

  return (
    <button
      type="button"
      onClick={goBack}
      className={className || "inline-flex items-center gap-1.5 text-sm text-forest-700 hover:underline"}
      data-testid={testid}
    >
      <ChevronLeft className="h-4 w-4" /> {label}
    </button>
  );
}
